import { useEffect, useMemo, useRef, useState } from 'react';
import { CheckSquare, Trash2, ArrowLeftRight, Plus, ImageOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { haptics } from '@/lib/haptics';
import { useStableObjectUrl } from '@/lib/useStableObjectUrl';


export interface TriagePair {
  key: string;
  front: File;
  back: File | null;
}

interface Props {
  photos: File[];
  initialMode: 'pairs' | 'singles';
  onChange: (pairs: TriagePair[]) => void;
}

function pairPhotos(files: File[]): TriagePair[] {
  const out: TriagePair[] = [];
  for (let i = 0; i < files.length; i += 2) {
    out.push({
      key: `p-${i}-${files[i].name}-${files[i].size}`,
      front: files[i],
      back: files[i + 1] ?? null,
    });
  }
  return out;
}

function singlePhotos(files: File[]): TriagePair[] {
  return files.map((f, i) => ({
    key: `s-${i}-${f.name}-${f.size}`,
    front: f,
    back: null,
  }));
}

export function BulkPhotoTriage({ photos, initialMode, onChange }: Props) {
  const [items, setItems] = useState<TriagePair[]>(() =>
    initialMode === 'pairs' ? pairPhotos(photos) : singlePhotos(photos)
  );
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const selectionOrderRef = useRef<string[]>([]);

  // Notify parent whenever the pairing changes.
  useEffect(() => {
    onChange(items);
  }, [items, onChange]);

  const counts = useMemo(() => {
    let paired = 0;
    let singles = 0;
    for (const it of items) {
      if (it.back) paired++;
      else singles++;
    }
    return { paired, singles, total: items.length };
  }, [items]);

  const isSingle = (it: TriagePair) => !it.back;

  const toggleSelect = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        selectionOrderRef.current = selectionOrderRef.current.filter((k) => k !== key);
      } else {
        const item = items.find((i) => i.key === key);
        if (!item || !isSingle(item)) return prev;
        next.add(key);
        selectionOrderRef.current.push(key);
      }
      return next;
    });
  };

  const pairSelected = () => {
    if (selectedKeys.size !== 2) return;
    const [firstKey, secondKey] = selectionOrderRef.current.slice(-2);
    const first = items.find((i) => i.key === firstKey);
    const second = items.find((i) => i.key === secondKey);
    if (!first || !second) return;

    setItems((prev) => {
      const filtered = prev.filter((i) => i.key !== firstKey && i.key !== secondKey);
      return [
        ...filtered,
        {
          key: `p-${Date.now()}-${first.front.name}`,
          front: first.front,
          back: second.front,
        },
      ];
    });
    setSelectedKeys(new Set());
    selectionOrderRef.current = [];
  };

  const split = (key: string) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.key === key);
      if (idx === -1) return prev;
      const item = prev[idx];
      if (!item.back) return prev;
      const next = [...prev];
      next.splice(idx, 1, { key: `s-${Date.now()}-a-${item.front.name}`, front: item.front, back: null });
      next.splice(idx + 1, 0, { key: `s-${Date.now()}-b-${item.back.name}`, front: item.back, back: null });
      return next;
    });
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const remove = (key: string) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const swap = (key: string) => {
    haptics.tap();
    setItems((prev) =>
      prev.map((i) =>
        i.key === key && i.back ? { ...i, front: i.back, back: i.front } : i
      )
    );
  };

  const swapAll = () => {
    haptics.tap();
    setItems((prev) =>
      prev.map((i) => (i.back ? { ...i, front: i.back, back: i.front } : i))
    );
  };


  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-muted-foreground tabular-nums">
          {counts.paired} pair{counts.paired === 1 ? '' : 's'} · {counts.singles} single
        </div>
        <div className="flex items-center gap-2">
          {counts.paired > 0 && (
            <button
              type="button"
              onClick={swapAll}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border border-white/[0.08] bg-white/[0.04] text-foreground/80 hover:bg-white/[0.08] active:scale-95 transition"
              title="Swap front/back on every pair"
            >
              <ArrowLeftRight className="w-3.5 h-3.5" />
              Swap all
            </button>
          )}

          {selectedKeys.size === 2 && (
            <button
              type="button"
              onClick={pairSelected}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border border-active-gold/40 bg-active-gold/10 text-active-gold hover:bg-active-gold/20 transition"
            >
              <Plus className="w-3.5 h-3.5" />
              Pair selected
            </button>
          )}
          {selectedKeys.size > 0 && selectedKeys.size !== 2 && (
            <div className="text-[11px] text-muted-foreground">
              Select {selectedKeys.size === 1 ? 'one more single' : 'exactly two singles'} to pair
            </div>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {items.map((item) =>
          item.back ? (
            <PairCard
              key={item.key}
              item={item}
              selected={selectedKeys.has(item.key)}
              onSplit={() => split(item.key)}
              onSwap={() => swap(item.key)}
              onRemove={() => remove(item.key)}
            />
          ) : (
            <SingleCard
              key={item.key}
              item={item}
              selected={selectedKeys.has(item.key)}
              onToggle={() => toggleSelect(item.key)}
              onRemove={() => remove(item.key)}
            />
          )
        )}
      </div>

      {items.length === 0 && (
        <div className="py-8 text-center text-sm text-muted-foreground">
          No photos left. Go back and choose more.
        </div>
      )}
    </div>
  );
}

function SingleCard({
  item,
  selected,
  onToggle,
  onRemove,
}: {
  item: TriagePair;
  selected: boolean;
  onToggle: () => void;
  onRemove: () => void;
}) {
  const url = useObjectUrl(item.front);
  return (
    <div
      className={cn(
        'relative rounded-xl overflow-hidden border bg-black/40 transition',
        selected
          ? 'border-active-gold/60 ring-1 ring-active-gold/60'
          : 'border-white/[0.08]'
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full aspect-square relative"
        aria-pressed={selected}
      >
        {url ? (
          <img src={url} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 grid place-items-center text-muted-foreground">
            <ImageOff className="w-6 h-6" />
          </div>
        )}
        <div
          className={cn(
            'absolute top-2 left-2 w-5 h-5 rounded-full border flex items-center justify-center transition',
            selected
              ? 'bg-active-gold border-active-gold text-black'
              : 'bg-black/60 border-white/30'
          )}
        >
          {selected && <CheckSquare className="w-3 h-3" />}
        </div>
      </button>
      <div className="absolute bottom-2 right-2 flex items-center gap-1">
        <button
          type="button"
          onClick={onRemove}
          className="p-1.5 rounded-full bg-black/70 hover:bg-destructive/80 text-white/90 border border-white/10 transition"
          aria-label="Remove photo"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function PairCard({
  item,
  selected,
  onSplit,
  onSwap,
  onRemove,
}: {
  item: TriagePair;
  selected: boolean;
  onSplit: () => void;
  onSwap: () => void;
  onRemove: () => void;
}) {
  const frontUrl = useObjectUrl(item.front);
  const backUrl = useObjectUrl(item.back);
  return (
    <div
      className={cn(
        'relative rounded-xl overflow-hidden border bg-black/40 transition',
        selected ? 'border-active-gold/60 ring-1 ring-active-gold/60' : 'border-white/[0.08]'
      )}
    >
      <div className="grid grid-cols-2 aspect-[2/1]">
        <div className="relative bg-black/60">
          {frontUrl ? (
            <img src={frontUrl} alt="front" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 grid place-items-center text-muted-foreground">
              <ImageOff className="w-5 h-5" />
            </div>
          )}
          <span className="absolute bottom-1 left-1 text-[9px] px-1 rounded bg-black/70 text-white/90">front</span>
        </div>
        <div className="relative bg-black/60 border-l border-white/[0.06]">
          {backUrl ? (
            <img src={backUrl} alt="back" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 grid place-items-center text-muted-foreground">
              <ImageOff className="w-5 h-5" />
            </div>
          )}
          <span className="absolute bottom-1 left-1 text-[9px] px-1 rounded bg-black/70 text-white/90">back</span>
        </div>
      </div>
      <div className="absolute top-2 right-2 flex flex-col gap-1">
        <button
          type="button"
          onClick={onSwap}
          className="p-1.5 rounded-full bg-black/70 hover:bg-active-gold/80 hover:text-black text-white/90 border border-white/10 active:scale-95 transition"
          aria-label="Swap front and back"
          title="Swap"
        >
          <ArrowLeftRight className="w-3.5 h-3.5" />
        </button>

      </div>
      <div className="absolute bottom-2 right-2 flex items-center gap-1">
        <button
          type="button"
          onClick={onSplit}
          className="px-2 py-1 rounded-full bg-black/70 hover:bg-white/20 text-white/90 border border-white/10 text-[10px] transition"
        >
          Split
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="p-1.5 rounded-full bg-black/70 hover:bg-destructive/80 text-white/90 border border-white/10 transition"
          aria-label="Remove pair"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

const useObjectUrl = useStableObjectUrl;

